import type { Lead, LeadObject } from '../api/types';
import { addressLine } from '../ops/format';
import { propertyTypeLabels } from '../owners/labels';
import { saleTimeframeText } from './labels';

export interface CallFact {
  label: string;
  value: string;
}

const area = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });
const euro = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });

function rawValue(raw: unknown, key: string): string {
  if (!raw || typeof raw !== 'object') return '';
  const v = (raw as Record<string, unknown>)[key];
  return typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '';
}

export const leadFactLabels = ['Adresse', 'Erreichbar', 'Früher gesprochen mit'];

// callFacts: alles Wichtige zum Objekt fürs Gespräch (Dominik 15.10.2026) – gespeicherte Werte, sonst der Originaltext aus
// Dialfire (z. B. „450.000 - 500.000 VB“), dazu Erreichbarkeit, mit wem der Eigentümer früher gesprochen hat (Name des
// Dialfire-Agenten, so wie er sich am Telefon vorgestellt hat) und was im Gespräch noch zu erfragen ist.
export function callFacts(
  o: LeadObject | undefined,
  lead: Pick<Lead, 'reachability' | 'attributes' | 'source_agent_name' | 'street' | 'house_number' | 'postal_code' | 'city'>,
): { facts: CallFact[]; missing: string[] } {
  const raw = lead.attributes?.raw;
  const facts: CallFact[] = [];
  const missing: string[] = [];
  const add = (label: string, value: string, ask?: string) => {
    if (value) facts.push({ label, value });
    else if (ask) missing.push(ask);
  };
  const subtype = o?.property_subtype && o.property_subtype !== 'Normal' ? o.property_subtype : '';
  const kind = o ? [propertyTypeLabels[o.property_type] ?? '', subtype, o.unit_number ?? '', o.unit_location ?? ''].filter(Boolean).join(' · ') : '';
  add('Objekt', kind, 'Objektart');
  add('Adresse', addressLine(lead).replace(/^–$/, ''));
  const withUnit = (text: string, unit: string) => (text ? `${text} ${unit}` : '');
  const size = [
    o?.living_area_sqm ? `${area.format(o.living_area_sqm)} m² Wohnfläche` : withUnit(rawValue(raw, 'Wfl'), 'Wohnfläche'),
    o?.rooms ? `${area.format(o.rooms)} Zimmer` : withUnit(rawValue(raw, 'Zi'), 'Zimmer'),
    o?.plot_area_sqm ? `${area.format(o.plot_area_sqm)} m² Grundstück` : withUnit(rawValue(raw, 'Grund'), 'Grundstück'),
  ].filter(Boolean);
  add('Größe', size.join(' · '), 'Fläche');
  add('Baujahr', o?.year_built ? String(o.year_built) : rawValue(raw, 'Bj'), 'Baujahr');
  add('Preisvorstellung', o?.price_expectation_eur ? `${euro.format(o.price_expectation_eur)} €` : rawValue(raw, 'Preisvorstellung'), 'Preisvorstellung');
  add('Verkauf', saleTimeframeText(o?.sale_timeframe));
  add('Erreichbar', lead.reachability?.trim() ?? '');
  add('Früher gesprochen mit', lead.source_agent_name?.trim() ?? '');
  add('Zum Objekt', o?.notes?.trim() ?? '');
  return { facts, missing };
}
